import React, { useState, useEffect, useCallback } from 'react';
import cn from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { ProcessIdentifiers, ProcessLoadStatus, ProcessStatus } from 'buyplan-common';
import moment from 'moment';
import Button from '../Button/Button';
import Loader from '../Loader/Loader';
import './SyncImages.scss';
import { getExternalImages } from '../../services/externalImagesService';
import { getProcessStatus } from '../../services/processesService';
import useUserSettings from '../../selectors/useUserSettings';

function SyncImages() {
    const [syncLoading, setSyncLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [syncStatus, setSyncStatus] = useState<ProcessStatus>();
    const { activeChannelId } = useUserSettings();

    const processError = !!errorMessage || syncStatus?.status === ProcessLoadStatus.error;
    const processComplete = syncStatus?.status === ProcessLoadStatus.complete;

    const getStatus = useCallback(async () => {
        try {
            const status = await getProcessStatus(ProcessIdentifiers.pullExternalImages);
            if (!status) return;
            setSyncStatus(status?.data);
        } catch (e: unknown) {
            const err = e as Error;
            setErrorMessage(err.message || 'The image status is unknown');
            setSyncLoading(false);
        }
    }, []);

    useEffect(() => {
        if (processError || processComplete) {
            setSyncLoading(false);
        }
        if (syncStatus?.status === ProcessLoadStatus.progress && !syncLoading) {
            setSyncLoading(true);
        }
    }, [processComplete, processError, syncLoading, syncStatus?.status]);

    useEffect(() => {
        let refreshStatus: NodeJS.Timeout;
        // fetch status initially
        getStatus();
        if (syncLoading) {
            refreshStatus = setInterval(() => {
                // check for status update every 10 seconds if a sync was run
                getStatus();
            }, 10000);
        }
        return () => clearInterval(refreshStatus);
    }, [getStatus, syncLoading, activeChannelId]);

    const handleImageSync = () => {
        if (errorMessage) setErrorMessage('');
        // set loading state. This process could take minutes, so do not wait
        setSyncLoading(true);
        try {
            getExternalImages();
        } catch (err: unknown) {
            const error = err as Error;
            setErrorMessage(error.message || 'There was an error retrieving images');
            setSyncLoading(false);
        }
    };

    return (
        <div className="SyncImages">
            <Button disabled={syncLoading} className="Button__inverted" onClick={handleImageSync}>
                Get images {syncLoading && <Loader width={16} />}
            </Button>
            <div className="SyncImages__statusContainer">
                {!syncLoading && (
                    <>
                        <div
                            className={cn('SyncImages__status', {
                                'SyncImages__status--success': processComplete,
                                'SyncImages__status--error': processError,
                            })}
                        >
                            {processComplete && (
                                <>
                                    <FontAwesomeIcon icon={faCheck as IconProp} /> Images Successfully retrieved
                                </>
                            )}
                            {processError && (
                                <>
                                    <FontAwesomeIcon icon={faExclamationTriangle as IconProp} />{' '}
                                    {syncStatus?.status === ProcessLoadStatus.error
                                        ? 'There was an error retrieving images'
                                        : errorMessage || 'There was an error'}
                                </>
                            )}
                        </div>
                        <div className="SyncImages__syncDate">
                            {syncStatus?.syncDate
                                ? `Last retrieved ${moment(syncStatus?.syncDate).calendar()}`
                                : 'Images have not been retrieved'}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

export default SyncImages;
